import { ExemptionsAndExemptionsRegisterStatus } from '@/enum/global.enum'
import { getGlobalStatusColor, getGlobalStatusHexColor } from '@/utils/global-status-color.utils'
import moment from 'moment'

/**
 * @param type - The type of exemption status
 * @returns The text representation of the exemption status
 */
export const getExemptionStatusLabel = (type: ExemptionsAndExemptionsRegisterStatus) => {
  switch (type) {
    case ExemptionsAndExemptionsRegisterStatus.DRAFT:
      return 'Draft'
    case ExemptionsAndExemptionsRegisterStatus.UNDER_REVIEW:
      return 'Under Review'
    case ExemptionsAndExemptionsRegisterStatus.PENDING_APPROVAL:
      return 'Pending Approval'
    case ExemptionsAndExemptionsRegisterStatus.APPROVED:
      return 'Approved'
    case ExemptionsAndExemptionsRegisterStatus.REJECTED:
      return 'Rejected'
    case ExemptionsAndExemptionsRegisterStatus.EXPIRED:
      return 'Expired'
    default:
      return type
  }
}

/**
 *
 * @param status - The status of the exemption
 * @returns The color of the exemption status
 */
export const getExemptionStatusColor = (status: string): string => {
  return getGlobalStatusColor(status)
}

export const exemptionStatusBgColorOptions: Partial<Record<ExemptionsAndExemptionsRegisterStatus, string>> = {
  [ExemptionsAndExemptionsRegisterStatus.DRAFT]: getGlobalStatusHexColor(ExemptionsAndExemptionsRegisterStatus.DRAFT),
  [ExemptionsAndExemptionsRegisterStatus.UNDER_REVIEW]: getGlobalStatusHexColor(
    ExemptionsAndExemptionsRegisterStatus.UNDER_REVIEW
  ),
  [ExemptionsAndExemptionsRegisterStatus.PENDING_APPROVAL]: getGlobalStatusHexColor(
    ExemptionsAndExemptionsRegisterStatus.PENDING_APPROVAL
  ),
  [ExemptionsAndExemptionsRegisterStatus.APPROVED]: getGlobalStatusHexColor(
    ExemptionsAndExemptionsRegisterStatus.APPROVED
  ),
  [ExemptionsAndExemptionsRegisterStatus.REJECTED]: getGlobalStatusHexColor(
    ExemptionsAndExemptionsRegisterStatus.REJECTED
  ),
  [ExemptionsAndExemptionsRegisterStatus.EXPIRED]: getGlobalStatusHexColor(
    ExemptionsAndExemptionsRegisterStatus.EXPIRED
  ),
}

/**
 * Whole calendar days from today until the expiry/review date - negative once overdue.
 * Compares calendar dates, not timestamps, so tomorrow is 1 day away whatever the time of day.
 * @param expiryOrReviewDate - The exemption's expiry/review date (date-only ISO value)
 * @returns Days until the date, or null when there is no date
 */
export const getExemptionDaysUntilExpiry = (expiryOrReviewDate?: string | Date | null): number | null => {
  if (!expiryOrReviewDate) return null
  const dateKey = moment.utc(expiryOrReviewDate).format('YYYY-MM-DD')
  return moment(dateKey, 'YYYY-MM-DD').diff(moment().startOf('day'), 'days')
}

/**
 * @param days - Result of getExemptionDaysUntilExpiry
 * @returns "Due today", "N days remaining" or "N days overdue"
 */
export const getExemptionDueLabel = (days: number): string => {
  if (days === 0) return 'Due today'
  const count = Math.abs(days)
  const unit = count === 1 ? 'day' : 'days'
  return days > 0 ? `${count} ${unit} remaining` : `${count} ${unit} overdue`
}

/**
 * Approved and Rejected exemptions are finished - no overdue or remaining days apply.
 * @param status - The exemption status
 * @returns True when the review/approval cycle is over
 */
export const isExemptionFinished = (status?: ExemptionsAndExemptionsRegisterStatus | null): boolean =>
  status === ExemptionsAndExemptionsRegisterStatus.APPROVED || status === ExemptionsAndExemptionsRegisterStatus.REJECTED
